/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */



(function() {

    let current_variant = null;
    const variant_script = document.querySelector('product-rerender variant-picker script[type="application/json"]');
    if (variant_script){
        try {
            current_variant = JSON.parse(variant_script.textContent);
            //console.log("Product JSON:", current_variant);
        } catch (e) {
            //console.error("Failed to parse product JSON", e);
        }
    }
    const name_max_length = 14;
    const number_max_length = 3;

    // --- Variant change handling ---
    document.addEventListener("variant:changed", function (event) {
        current_variant = event.detail.variant;
        console.log("Current variant:", current_variant);
        retainData();
    });

    // --- Limit input length dynamically (delegated) ---
    document.addEventListener("input", function (event) {
        const input = event.target;
        if (!input.closest(".custom_product_attribute .form-container")) return;

        if (input.id === "attr-name" && input.value.length > name_max_length) {
            input.value = input.value.substring(0, name_max_length);
        }
        if (input.id === "attr-number" && input.value.length > number_max_length) {
            input.value = input.value.substring(0, number_max_length);
        }
    });

    // --- Next button click ---
    document.addEventListener("click", function (event) {
        if (!event.target.closest("#custom_attr_submit_component")) return;

        const container = document.querySelector(".custom_product_attribute");
        if (!container) return;

        const name = container.querySelector("#attr-name")?.value || "";
        const number = container.querySelector("#attr-number")?.value || "none";
        const style = container.querySelector("#attr-jersey-style")?.value || "";
        const color = container.querySelector("#attr-jersey-color")?.value || "";

        if (!name) {
            alert("Please fill in the Name field.");
            return;
        }
        if (!style) {
            alert("Please select a Jersey Style.");
            return;
        }

        document.querySelector("#custom_modal2-attr-name").innerText = name;
        document.querySelector("#custom_modal2-attr-number").innerText = number;
        document.querySelector("#custom_modal2-attr-style").innerText = style;
        document.querySelector("#custom_modal2-attr-color").innerText = color;

        document.querySelector("#custom_modal1").style.display = "none";
        document.querySelector("#custom_modal2").style.display = "block";
    });

    // --- Back to edit ---
    document.addEventListener("click", function (event) {
        if (event.target.closest("#back-to-edit-button")) {
            document.querySelector("#custom_modal2").style.display = "none";
            document.querySelector("#custom_modal1").style.display = "block";
        }
    });

    // --- Open modal (clear inserted properties) ---
    document.addEventListener("click", function (event) {
        if (!event.target.closest("#open-modal-btn")) return;

        const form = document.querySelector('.product-form__buy-buttons form[action="/cart/add"]');
        if (form) form.querySelectorAll(".inserted-property").forEach(el => el.remove());
        // clearFields();
    });

    // --- Add to Jersey button ---
    document.addEventListener("click", function (event) {
        if (!event.target.closest("#custom_attr_add_cart")) return;

        const container = document.querySelector(".custom_product_attribute");
        const form = document.querySelector('.product-form__buy-buttons form[action="/cart/add"]');
        if (!container || !form) return;

        const drawer = container.querySelector("#product-drawer");
        if (drawer) drawer.removeAttribute("open");

        document.querySelector("#custom_modal1").style.display = "block";
        document.querySelector("#custom_modal2").style.display = "none";

        const open_modal_btn = container.querySelector("#open-modal-btn");
        if (open_modal_btn) {
            open_modal_btn.innerHTML = "Name & Number +$44.95 will appear in your cart";
            open_modal_btn.style.background = "#d3d3d3";
            open_modal_btn.style.color = "#22272D";
        }

        // Create hidden inputs
        const nameVal = container.querySelector("#attr-name")?.value || "";
        const numberVal = container.querySelector("#attr-number")?.value || "";
        const styleVal = container.querySelector("#attr-jersey-style")?.value || "";
        const colorVal = container.querySelector("#attr-jersey-color")?.value || "";

        const styleField = container.querySelector("#attr-jersey-style");
        const selectedOption = styleField.options[styleField.selectedIndex];
        const var_id = selectedOption.getAttribute("data-product-id");

        if(nameVal){
            setCustomData('name', nameVal);
        }
        if(numberVal){
            setCustomData('number', numberVal);
        }
        if(styleVal){
            setCustomData('style', styleVal);
        }
        if(colorVal){
            setCustomData('color', colorVal);
        }

        // const fields = [
        //     { name: "properties[Name]", value: nameVal },
        //     { name: "properties[Number]", value: numberVal },
        //     { name: "properties[Style]", value: styleVal },
        //     { name: "properties[Colour]", value: colorVal },
        //     { name: "properties[_comes_with]", value: var_id },
        // ];

        // if (current_variant) {
        //     fields.push(
        //         { name: "properties[Jersey]", value: current_variant.name },
        //         { name: "properties[Jersey Size]", value: current_variant.title },
        //         { name: "properties[Jersey SKU]", value: current_variant.sku }
        //     );
        // }
        const fields = [
            { name: "properties[_comes_with]", value: var_id },
        ];

        fields.forEach(f => {
            const input = document.createElement("input");
            input.classList.add("inserted-property");
            input.type = "hidden";
            input.name = f.name;
            input.value = f.value;
            form.appendChild(input);
        });
    });
    document.addEventListener("click", function (event) {
        if (!event.target.closest(".product-form__buy-buttons #AddToCart")) return;

        document.addEventListener('variant:added', function(event) {
           var variant = event.detail.variant; // Get the variant that was added

            const container = document.querySelector(".custom_product_attribute");
            const styleField = container.querySelector("#attr-jersey-style");
            const selectedOption = styleField.options[styleField.selectedIndex];
            const var_id = selectedOption.getAttribute("data-product-id");

            const nameVal = container.querySelector("#attr-name")?.value || "";
            const numberVal = container.querySelector("#attr-number")?.value || "";
            const styleVal = container.querySelector("#attr-jersey-style")?.value || "";
            const colorVal = container.querySelector("#attr-jersey-color")?.value || "";
            const quantity = 1;
            const properties = {
                    Name: nameVal,
                    Number: numberVal,
                    Style: styleVal,
                    Colour: colorVal,
                    Jersey: window.product ? window.product.product.title : current_variant.name,
                    "Jersey Size": current_variant.title,
                    "Jersey SKU": current_variant.sku,

            };
           if(current_variant.id === variant.id){
                fetch("/cart/add.js", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({
                        id: var_id,
                        quantity: 1,
                        properties
                    })
                }).then(response => response.json()).then(data => {
                    console.log("Extra product added:", data);
                    const form = document.querySelector('.product-form__buy-buttons form[action="/cart/add"]');
                    if (form) form.querySelectorAll(".inserted-property").forEach(el => el.remove());
                    setTimeout(() => {
                        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
                            bubbles: true
                        }));
                        document.getElementById('mini-cart').open = true;
                    }, 1000);
                    clearFields();
                }).catch(err => console.error("Error adding product:", err));
           }
        });
    
    }); 
    function updateCartDrawer() {
        fetch("/cart.js", {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            }
        }).then(response => response.json()).then(cart => {
            console.log("Cart data:", cart);
            document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
                bubbles: true
            }));
            document.getElementById('mini-cart').open = true;
        }).catch(error => {
            console.error("Error updating cart drawer:", error)
        })
    }
    function clearFields(){
        const container = document.querySelector(".custom_product_attribute");
        container.querySelector("#attr-name").value = "";
        container.querySelector("#attr-number").value = "";
        container.querySelector("#attr-jersey-style").value = "";
        if(container.querySelector("#attr-jersey-color")){
            container.querySelector("#attr-jersey-color").value = "";
        }      
    }  
    function setCustomData(key, value) {
        const script = document.getElementById('jersey-data');
        if(script){
            const data = JSON.parse(script.textContent || '{}');
            data[key] = value;
            script.textContent = JSON.stringify(data);
        }
    }
    function getCustomData() {
        const script = document.getElementById('jersey-data');
        return JSON.parse(script.textContent || '{}');
    }
    function retainData(){
      setTimeout(() => {    
        const data = getCustomData();
        const container = document.querySelector(".custom_product_attribute");
        console.log(data);
        if(Object.keys(data).length !== 0){
                if(data.name){
                    container.querySelector("#attr-name").value = data.name;
                }
                if(data.number){
                    container.querySelector("#attr-number").value = data.number;
                }
                if(data.style){
                    container.querySelector("#attr-jersey-style").value = data.style;
                }
                if(data.color){
                    container.querySelector("#attr-jersey-color").value = data.color;
                }
                
                const open_modal_btn = container.querySelector("#open-modal-btn");
                if (open_modal_btn) {
                    open_modal_btn.innerHTML = "Name & Number +$44.95 will appear in your cart";
                    open_modal_btn.style.background = "#d3d3d3";
                    open_modal_btn.style.color = "#22272D";
                }
          
        }
      }, 800);    
    } 
})();
   